import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { CrackedLogo } from "../components/CrackedLogo";
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  User, 
  Mail, 
  Plus, 
  X,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format time from ISO string (times are already in IST from backend)
const formatTimeIST = (isoString) => {
  // Parse the ISO string without timezone conversion
  const parts = isoString.split('T');
  if (parts.length < 2) return isoString;
  
  const timeParts = parts[1].split(':');
  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  
  return `${hours}:${minutes} ${ampm}`;
};

// Helper to format date from ISO string (dates are in IST from backend)
const formatDateIST = (isoString) => {
  const parts = isoString.split('T');
  if (parts.length < 1) return isoString;
  
  const dateParts = parts[0].split('-');
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1;
  const day = parseInt(dateParts[2], 10);
  
  const date = new Date(year, month, day);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });
};

export default function BookingPage() {
  const navigate = useNavigate();
  const { bookingLink } = useParams();
  const [searchParams] = useSearchParams();
  
  const [user, setUser] = useState(null);
  const [interviewers, setInterviewers] = useState([]);
  const [selectedInterviewer, setSelectedInterviewer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [intervieweeEmail, setIntervieweeEmail] = useState('');
  const [crmCheck, setCrmCheck] = useState({ status: 'idle', message: '' });
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [step, setStep] = useState(1);

  const fetchUser = useCallback(async (userId) => {
    try {
      const res = await axios.get(`${API}/users/me?user_id=${userId}`);
      setUser(res.data);
      return res.data;
    } catch (error) {
      return null;
    }
  }, []);

  const fetchInterviewers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/interviewers`);
      return res.data;
    } catch (error) {
      toast.error("Failed to load interviewers");
      return [];
    }
  }, []);

  // Validate user session - check if user has valid tokens
  const validateSession = useCallback(async (userId) => {
    try {
      const res = await axios.get(`${API}/auth/validate?user_id=${userId}`);
      return res.data.valid;
    } catch (error) {
      return false;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      let userId = searchParams.get('user_id');
      if (userId) {
        localStorage.setItem('cracked_user_id', userId);
      } else {
        userId = localStorage.getItem('cracked_user_id');
      }

      const initiateGoogleLogin = async (redirectPath) => {
        try {
          const res = await axios.get(`${API}/auth/google/login?role=counsellor&redirect_path=${encodeURIComponent(redirectPath)}`);
          window.location.href = res.data.authorization_url;
        } catch (error) {
          toast.error("Failed to initiate login. Please try again.");
        }
      };

      // If no user ID, require login
      if (!userId) {
        const redirectPath = bookingLink ? `/book/${bookingLink}` : '/book';
        initiateGoogleLogin(redirectPath);
        return;
      }

      // Fetch user data
      const userData = await fetchUser(userId);
      if (!userData) {
        localStorage.removeItem('cracked_user_id');
        const redirectPath = bookingLink ? `/book/${bookingLink}` : '/book';
        initiateGoogleLogin(redirectPath);
        return;
      }

      // Validate session has valid tokens
      const isValid = await validateSession(userId);
      if (!isValid) {
        localStorage.removeItem('cracked_user_id');
        toast.error("Session expired. Please sign in again.");
        const redirectPath = bookingLink ? `/book/${bookingLink}` : '/book';
        initiateGoogleLogin(redirectPath);
        return;
      }

      // If booking link provided, fetch that specific interviewer
      if (bookingLink) {
        try {
          const res = await axios.get(`${API}/users/interviewer/${bookingLink}`);
          setInterviewers([res.data]);
          setSelectedInterviewer(res.data);
          setStep(2);
        } catch (error) {
          toast.error("Invalid booking link");
          const all = await fetchInterviewers();
          setInterviewers(all);
        }
      } else {
        const all = await fetchInterviewers();
        setInterviewers(all);
      }

      setLoading(false);
    };

    init();
  }, [bookingLink, searchParams, fetchUser, fetchInterviewers, validateSession]);

  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedInterviewer || !selectedDate) {
        setAvailableSlots([]);
        return;
      }

      setSlotsLoading(true);
      try {
        // Format date in local timezone for the API
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        const res = await axios.get(`${API}/availability/${selectedInterviewer.id}/slots?date=${dateStr}`);
        setAvailableSlots(res.data.slots || []);
      } catch (error) {
        toast.error("Failed to load available slots");
        setAvailableSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };

    fetchSlots();
  }, [selectedInterviewer, selectedDate]);

  const handleSelectInterviewer = (interviewer) => {
    setSelectedInterviewer(interviewer);
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep(2);
  };

  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleBooking = async () => {
    if (!selectedInterviewer || !selectedSlot || !user) return;

    // Validate mandatory email
    if (!intervieweeEmail || !intervieweeEmail.trim() || !intervieweeEmail.includes('@')) {
      toast.error("Please enter a valid interviewee email ID");
      return;
    }

    setBooking(true);
    try {
      const res = await axios.post(`${API}/bookings?user_id=${user.id}`, {
        interviewer_id: selectedInterviewer.id,
        slot_start: selectedSlot.start,
        slot_end: selectedSlot.end,
        additional_emails: [intervieweeEmail.trim()]
      });

      navigate('/confirmation', { state: { booking: res.data, interviewer: selectedInterviewer } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book slot");
    } finally {
      setBooking(false);
    }
  };

  const goBack = () => {
    if (step === 3) {
      setStep(2);
      setSelectedSlot(null);
    } else if (step === 2 && !bookingLink) {
      setStep(1);
      setSelectedInterviewer(null);
      setSelectedDate(null);
    }
  };

  // Debounced CRM eligibility check while the counsellor types the interviewee email.
  // Soft hint only — backend still hard-gates the actual booking.
  useEffect(() => {
    const email = (intervieweeEmail || '').trim();
    if (!email || !email.includes('@') || !user?.id) {
      setCrmCheck({ status: 'idle', message: '' });
      return;
    }
    
    setCrmCheck({ status: 'checking', message: 'Checking lead in CRM…' });
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/crm/check-lead-ownership?user_id=${user.id}&interviewee_email=${encodeURIComponent(email)}`
        );
        setCrmCheck({
          status: res.data.status || 'error',
          message: res.data.message || '',
        });
      } catch (e) {
        setCrmCheck({ status: 'error', message: 'Could not verify lead in CRM' });
      }
    }, 600);
    
    return () => clearTimeout(handle);
  }, [intervieweeEmail, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-cracked-deep-teal text-white px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <CrackedLogo inverted />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
              <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('cracked_user_id');
                window.location.href = '/';
              }}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title="Logout"
              data-testid="logout-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`step-dot ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`} />
          <div className={`step-dot ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`} />
          <div className={`step-dot ${step >= 3 ? 'active' : ''}`} />
        </div>

        {/* Back Button */}
        {(step > 1 && (step > 2 || !bookingLink)) && (
          <Button
            variant="ghost"
            onClick={goBack}
            className="mb-4 text-cracked-deep-teal"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}

        {/* Step 1: Select Interviewer */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h1 className="text-2xl font-bold text-cracked-deep-teal mb-2">Select an Interviewer</h1>
            <p className="text-cracked-deep-teal/60 mb-8">Choose who you'd like to book an interview with</p>

            {interviewers.length === 0 ? (
              <Card className="p-12 text-center">
                <User className="w-12 h-12 mx-auto mb-4 text-cracked-deep-teal/30" />
                <p className="text-cracked-deep-teal/50">No interviewers available</p>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {interviewers.map((interviewer) => (
                  <Card
                    key={interviewer.id}
                    className="interviewer-card p-4 border-2 hover:border-cracked-light-teal"
                    onClick={() => handleSelectInterviewer(interviewer)}
                    data-testid={`interviewer-${interviewer.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="w-14 h-14">
                        <AvatarImage src={interviewer.picture} alt={interviewer.name} />
                        <AvatarFallback className="bg-cracked-light-teal text-white text-lg">
                          {interviewer.name?.charAt(0) || 'I'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-cracked-deep-teal">{interviewer.name}</h3>
                        <p className="text-sm text-cracked-deep-teal/60">{interviewer.email}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-cracked-deep-teal/30" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && selectedInterviewer && (
          <div className="animate-fade-in">
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="w-12 h-12">
                <AvatarImage src={selectedInterviewer.picture} alt={selectedInterviewer.name} />
                <AvatarFallback className="bg-cracked-light-teal text-white">
                  {selectedInterviewer.name?.charAt(0) || 'I'}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-xl font-bold text-cracked-deep-teal">Book with {selectedInterviewer.name}</h1>
                <p className="text-sm text-cracked-deep-teal/60">Select a date and time slot</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Calendar */}
              <Card data-testid="calendar-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-cracked-deep-teal">
                    <CalendarDays className="w-5 h-5" />
                    Select Date
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    className="rounded-md border"
                    data-testid="date-picker"
                  />
                </CardContent>
              </Card>

              {/* Time Slots */}
              <Card data-testid="slots-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-cracked-deep-teal">
                    <Clock className="w-5 h-5" />
                    Available Slots
                  </CardTitle>
                  {selectedDate && (
                    <CardDescription>
                      {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {!selectedDate ? (
                    <div className="text-center py-8 text-cracked-deep-teal/50">
                      <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>Please select a date first</p>
                    </div>
                  ) : slotsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="spinner" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-8 text-cracked-deep-teal/50">
                      <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>No slots available on this date</p>
                      <p className="text-sm">Try selecting a different date</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[300px]">
                      <div className="availability-grid">
                        {availableSlots.map((slot, index) => (
                          <button
                            key={index}
                            className={`time-slot ${selectedSlot?.start === slot.start ? 'selected' : ''}`}
                            onClick={() => handleSelectSlot(slot)}
                            data-testid={`slot-${index}`}
                          >
                            {formatTimeIST(slot.start)}
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: Add Interviewee Email & Confirm */}
        {step === 3 && selectedInterviewer && selectedSlot && (
          <div className="animate-fade-in max-w-lg mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-cracked-deep-teal">Booking Details</CardTitle>
                <CardDescription>Review and confirm your booking</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                <div className="p-4 rounded-xl bg-cracked-light-teal/5 border border-cracked-light-teal/20">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={selectedInterviewer.picture} alt={selectedInterviewer.name} />
                      <AvatarFallback className="bg-cracked-light-teal text-white">
                        {selectedInterviewer.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-cracked-deep-teal">{selectedInterviewer.name}</p>
                      <p className="text-sm text-cracked-deep-teal/60">{selectedInterviewer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-cracked-deep-teal">
                    <CalendarDays className="w-4 h-4" />
                    <span className="text-sm">
                      {formatDateIST(selectedSlot.start)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-cracked-deep-teal mt-1">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">
                      {formatTimeIST(selectedSlot.start)} - {formatTimeIST(selectedSlot.end)}
                    </span>
                  </div>
                </div>

                {/* Interviewee Email - Mandatory */}
                <div className="space-y-3">
                  <Label className="text-cracked-deep-teal flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Interviewee Email ID <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-cracked-deep-teal/50">
                    Enter the candidate's email to include them in the calendar invite
                  </p>
                  <div className="relative">
                    <Input
                      type="email"
                      placeholder="candidate@email.com"
                      value={intervieweeEmail}
                      onChange={(e) => setIntervieweeEmail(e.target.value)}
                      required
                      className="pr-10"
                      data-testid="interviewee-email"
                    />
                    {crmCheck.status !== 'idle' && (
                      <div
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        data-testid="crm-check-indicator"
                      >
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center justify-center cursor-help">
                                {crmCheck.status === 'checking' && (
                                  <Loader2 className="w-5 h-5 text-cracked-deep-teal/40 animate-spin" />
                                )}
                                {crmCheck.status === 'eligible' && (
                                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                                )}
                                {(crmCheck.status === 'not_in_crm' || crmCheck.status === 'wrong_counsellor') && (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                                {crmCheck.status === 'error' && (
                                  <AlertCircle className="w-5 h-5 text-amber-500" />
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {crmCheck.message ||
                                (crmCheck.status === 'eligible'
                                  ? 'Lead assigned to you'
                                  : 'Lead not assigned to you')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                </div>

                {/* Book Button */}
                <Button
                  onClick={handleBooking}
                  disabled={booking || !intervieweeEmail.trim()}
                  className="w-full bg-cracked-light-teal hover:bg-cracked-deep-teal text-white py-6 text-lg"
                  data-testid="proceed-booking-btn"
                >
                  {booking ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      Book {selectedInterviewer.name?.split(' ')[0]}'s calendar
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
